import {
  Users,
  ShieldCheck,
  Heart,
  ArrowUpRight,
  Sparkles,
  Bell,
  Headset,
  CalendarDays,
} from 'lucide-react'
import type { ComponentType } from 'react'
import { Link } from 'react-router-dom'
import { DiscordIcon, SteamIcon } from './BrandIcons'
import type { CommunityData, LeadershipMember } from '@/useCommunity'
import { useLanguage } from '@/localization'

/** Icon components used for social pills share this minimal prop shape. */
type SocialIcon = ComponentType<{ size?: number; className?: string }>

/**
 * Canonical community sections for the hub — the single authoritative home for
 * Uliunai's community identity. Each section carries an anchor id so the game
 * sites can deep-link up to it (e.g. `uliunai.com/#conduct`).
 *
 * - CommunitySection (#community) — the "Who we are" story
 * - DiscordCta       (#discord)   — the reusable "Join our Discord" CTA block
 * - TeamSection      (#team)      — platform leadership
 * - ConductSection   (#conduct)   — universal code of conduct
 */

/** Map an icon key from the API to its real brand logo. */
const SOCIAL_ICONS: Record<string, SocialIcon> = {
  discord: DiscordIcon,
  steam: SteamIcon,
}

function socialIcon(key: string): SocialIcon {
  return SOCIAL_ICONS[key.toLowerCase()] ?? DiscordIcon
}

/** Shared eyebrow + metal heading, matching the rest of the hub. */
function SectionHeading({
  icon,
  eyebrow,
  title,
}: {
  icon: React.ReactNode
  eyebrow: string
  title: string
}) {
  const { t } = useLanguage()

  return (
    <div className="text-center">
      <span className="inline-flex items-center gap-2 rounded-full border border-stone-line bg-white px-4 py-1.5 font-rajdhani text-xs font-semibold uppercase tracking-[0.25em] text-uliunai-red shadow-card">
        {icon}
        {t(eyebrow)}
      </span>
      <h2 className="metal-text mt-5 font-orbitron text-4xl font-black tracking-tight sm:text-5xl">
        {t(title)}
      </h2>
    </div>
  )
}

/* ------------------------------------------------------------------------- */

/**
 * #community — the canonical short "Who we are" story. This is the single
 * authoritative version; game sites keep a trimmed blurb and link back here.
 */
export function CommunitySection() {
  const { t } = useLanguage()

  return (
    <section id="community" className="scroll-mt-20 px-6 py-20">
      <div className="mx-auto max-w-4xl">
        <SectionHeading
          icon={<Users size={18} />}
          eyebrow="Who we are"
          title="The Uliunai Community"
        />

        <div className="mx-auto mt-10 max-w-3xl space-y-5 text-center font-rajdhani text-lg leading-relaxed text-stone-700">
          <p>
            {t('Uliunai has been bringing players together since')}{' '}
            <span className="font-bold text-uliunai-red">2010</span> - {t('home to the')}{' '}
            <span className="font-semibold text-stone-900">
              {t('longest-running Killing Floor server')}
            </span>{' '}
            {t('anywhere, and now a growing family of community servers across many games.')}
          </p>
          <p>
            {t(
              "We're built on something simple: a friendly, welcoming place to play. No elitism, no toxicity - just good people, fair games, and servers that have stayed online, tuned, and loved for well over a decade.",
            )}
          </p>
        </div>

        {/* Values strip */}
        <div className="mx-auto mt-12 grid max-w-3xl grid-cols-1 gap-4 sm:grid-cols-3">
          {[
            {
              Icon: Heart,
              title: 'Friendly First',
              body: 'A welcoming home for veterans and newcomers alike.',
            },
            {
              Icon: ShieldCheck,
              title: 'Fair & Clean',
              body: 'Well-moderated servers where everyone gets a fair game.',
            },
            {
              Icon: Sparkles,
              title: 'Built to Last',
              body: 'Continuously maintained and improved since 2010.',
            },
          ].map(({ Icon, title, body }) => (
            <div
              key={title}
              className="rounded-2xl border border-stone-line bg-white p-6 text-center shadow-card transition-all duration-200 hover:-translate-y-1 hover:border-uliunai-red/30 hover:shadow-card-hover"
            >
              <span className="mx-auto mb-3 inline-flex h-11 w-11 items-center justify-center rounded-full bg-uliunai-red/10 text-uliunai-red">
                <Icon size={20} />
              </span>
              <h3 className="font-orbitron text-base font-bold text-stone-900">{t(title)}</h3>
              <p className="mt-2 font-rajdhani text-sm text-stone-600">{t(body)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------------- */

/**
 * #discord — the canonical "Join our Discord" call-to-action. A visually strong,
 * reusable block (the pattern the game sites mirror, skinned per game).
 */
export function DiscordCta({ discordInvite }: { discordInvite: string }) {
  const { t } = useLanguage()

  return (
    <section id="discord" className="scroll-mt-20 px-6 py-20">
      <div className="mx-auto max-w-5xl">
        {/* Shared blue Discord community banner (same across every Uliunai site). */}
        <div className="relative overflow-hidden rounded-lg border border-red-900/30 bg-gray-950 bg-gradient-to-r from-[#5865F2]/30 via-black/70 to-red-900/30 p-8 md:p-12">
          <div className="absolute inset-0 bg-gradient-to-r from-[#5865F2]/10 to-red-600/10 opacity-50" />
          <div className="relative z-10 flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="flex items-center gap-5">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-[#5865F2] text-white shadow-lg shadow-[#5865F2]/30">
                <DiscordIcon size={30} />
              </div>
              <div className="text-center md:text-left">
                <h2 className="mb-1 font-orbitron text-2xl font-bold text-white md:text-3xl">
                  {t('Join Our Community')}
                </h2>
                <p className="text-lg text-gray-300">
                  {t('Chat with players, get server updates, find teammates, and connect with admins')}
                </p>
              </div>
            </div>
            <a
              href={discordInvite}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 whitespace-nowrap rounded-lg bg-[#5865F2] px-8 py-4 text-lg font-bold text-white shadow-lg shadow-[#5865F2]/25 transition-colors hover:bg-[#4752C4]"
            >
              <DiscordIcon size={20} />
              {t('Join Discord')}
            </a>
          </div>
          <div className="relative z-10 mt-6 flex flex-wrap justify-center gap-6 text-sm text-gray-200 md:justify-start">
            <span className="flex items-center gap-2"><Users size={16} className="text-[#5865F2]" /> {t('Active Community')}</span>
            <span className="flex items-center gap-2"><Bell size={16} className="text-[#5865F2]" /> {t('Server Announcements')}</span>
            <span className="flex items-center gap-2"><Headset size={16} className="text-[#5865F2]" /> {t('Admin Support')}</span>
            <span className="flex items-center gap-2"><CalendarDays size={16} className="text-[#5865F2]" /> {t('Event Updates')}</span>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------------- */

/** 2-letter fallback derived from a name when no `initials` are provided. */
function memberInitials(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

/**
 * Avatar that shows the member photo when available and gracefully falls back to
 * initials. The initials sit underneath; the image covers them once it loads and
 * hides itself on error, revealing the initials again — no state needed.
 */
function MemberAvatar({
  member,
  className = '',
}: {
  member: LeadershipMember
  className?: string
}) {
  return (
    <span
      className={`relative inline-flex items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-uliunai-red to-uliunai-red-dark font-orbitron font-black text-white ${className}`}
    >
      <span aria-hidden>{member.initials ?? memberInitials(member.name)}</span>
      {member.avatar && (
        <img
          src={member.avatar}
          alt={member.name}
          loading="lazy"
          onError={(e) => {
            e.currentTarget.style.display = 'none'
          }}
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
    </span>
  )
}

/** Centered divider/sub-heading separating the team tiers. */
function TierLabel({ children }: { children: React.ReactNode }) {
  const { t } = useLanguage()

  return (
    <div className="mt-14 flex items-center justify-center gap-3">
      <span className="h-px w-8 bg-stone-line" />
      <span className="font-rajdhani text-xs font-bold uppercase tracking-[0.3em] text-stone-500">
        {typeof children === 'string' ? t(children) : children}
      </span>
      <span className="h-px w-8 bg-stone-line" />
    </div>
  )
}

/**
 * #team — the full community team. The hub is the canonical "people" page, so it
 * shows everyone grouped by tier (core / admin / moderator) with descending card
 * prominence. Game sites render only their scoped Core team and link back here.
 */
export function TeamSection({ leadership }: { leadership: LeadershipMember[] }) {
  const { t } = useLanguage()
  const core = leadership.filter((m) => m.tier === 'core')
  const admins = leadership.filter((m) => m.tier === 'admin')
  const mods = leadership.filter((m) => m.tier === 'moderator')

  return (
    <section
      id="team"
      className="scroll-mt-20 border-y border-stone-line bg-parchment-deep/60 px-6 py-20"
    >
      <div className="mx-auto max-w-6xl">
        <SectionHeading icon={<Users size={18} />} eyebrow="The people" title="Meet the Team" />

        <p className="mx-auto mt-5 max-w-xl text-center font-rajdhani text-base text-stone-600">
          {t(
            'The crew that keeps Uliunai running across every game - from the core team to the admins and moderators who look after the community day to day.',
          )}
        </p>

        {/* Core team — big rich cards */}
        <div className="mx-auto mt-12 grid max-w-4xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {core.map((member) => (
            <CoreCard key={member.name} member={member} />
          ))}
        </div>

        {/* Administrators — medium cards */}
        {admins.length > 0 && (
          <>
            <TierLabel>Administrators</TierLabel>
            <div className="mx-auto mt-8 grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2">
              {admins.map((member) => (
                <AdminCard key={member.name} member={member} />
              ))}
            </div>
          </>
        )}

        {/* Moderators — compact cards */}
        {mods.length > 0 && (
          <>
            <TierLabel>Moderators</TierLabel>
            <div className="mx-auto mt-8 flex max-w-3xl flex-wrap justify-center gap-3">
              {mods.map((member) => (
                <ModCard key={member.name} member={member} />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  )
}

/** Core team member — full rich card (avatar ring, badge pill, role, bio, specialties). */
function CoreCard({ member }: { member: LeadershipMember }) {
  const { t } = useLanguage()

  return (
    <div className="flex flex-col items-center rounded-2xl border border-stone-line bg-white p-7 text-center shadow-card transition-all duration-200 hover:-translate-y-1 hover:border-uliunai-red/30 hover:shadow-card-hover">
      <div className="relative mb-6">
        <MemberAvatar
          member={member}
          className="h-24 w-24 border-4 border-uliunai-red text-2xl shadow-glow"
        />
        {member.badge && (
          <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-uliunai-red px-3 py-0.5 font-rajdhani text-xs font-bold uppercase tracking-wide text-white shadow-card">
            {t(member.badge)}
          </span>
        )}
      </div>
      <h3 className="font-orbitron text-xl font-bold text-stone-900">{member.name}</h3>
      <p className="mt-1 font-rajdhani text-sm font-semibold uppercase tracking-wider text-uliunai-red">
        {t(member.role)}
      </p>
      {member.description && (
        <p className="mt-3 font-rajdhani text-sm leading-relaxed text-stone-600">
          {t(member.description)}
        </p>
      )}
      {member.specialties && member.specialties.length > 0 && (
        <div className="mt-5 w-full">
          <div className="mb-2 font-rajdhani text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-500">
            {t('Specialties')}
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {member.specialties.map((s) => (
              <span
                key={s}
                className="rounded-full border border-stone-line bg-parchment px-3 py-1 font-rajdhani text-xs font-medium text-stone-700"
              >
                {t(s)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/** Administrator — medium card (avatar, badge chip, role, short blurb). */
function AdminCard({ member }: { member: LeadershipMember }) {
  const { t } = useLanguage()

  return (
    <div className="flex items-center gap-4 rounded-2xl border border-stone-line bg-white p-5 shadow-card transition-all duration-200 hover:-translate-y-0.5 hover:border-uliunai-red/30 hover:shadow-card-hover">
      <MemberAvatar
        member={member}
        className="h-16 w-16 shrink-0 border-2 border-uliunai-red/60 text-base"
      />
      <div className="min-w-0 text-left">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-orbitron text-base font-bold text-stone-900">{member.name}</h3>
          {member.badge && (
            <span className="rounded-full bg-uliunai-red/10 px-2 py-0.5 font-rajdhani text-[10px] font-bold uppercase tracking-wide text-uliunai-red">
              {t(member.badge)}
            </span>
          )}
        </div>
        <p className="font-rajdhani text-xs font-semibold uppercase tracking-wider text-uliunai-red">
          {t(member.role)}
        </p>
        {member.description && (
          <p className="mt-1 font-rajdhani text-sm leading-snug text-stone-600">
            {t(member.description)}
          </p>
        )}
      </div>
    </div>
  )
}

/** Moderator — compact card (small avatar + name + role). */
function ModCard({ member }: { member: LeadershipMember }) {
  const { t } = useLanguage()

  return (
    <div className="flex items-center gap-3 rounded-xl border border-stone-line bg-white px-4 py-3 shadow-card transition-colors duration-200 hover:border-uliunai-red/30">
      <MemberAvatar
        member={member}
        className="h-10 w-10 shrink-0 border border-uliunai-red/40 text-xs"
      />
      <div className="text-left">
        <div className="font-orbitron text-sm font-bold text-stone-900">{member.name}</div>
        <div className="font-rajdhani text-[11px] font-semibold uppercase tracking-wider text-uliunai-red">
          {t(member.role)}
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------------- */

/**
 * #conduct — a teaser for the universal code of conduct shared by every game.
 * Shows the headline pillars (first three categories); the full rulebook lives
 * on the dedicated /conduct page. A note clarifies each game additionally has
 * its own game-specific rules.
 */
export function ConductSection({
  conduct,
}: {
  conduct: CommunityData['conduct']
}) {
  const { t } = useLanguage()
  const pillars = conduct.slice(0, 3)

  return (
    <section id="conduct" className="scroll-mt-20 px-6 py-20">
      <div className="mx-auto max-w-5xl">
        <SectionHeading
          icon={<ShieldCheck size={18} />}
          eyebrow="The ground rules"
          title="Code of Conduct"
        />

        <p className="mx-auto mt-5 max-w-2xl text-center font-rajdhani text-base text-stone-600">
          {t(
            "A shared code of conduct that applies across every Uliunai server. Each game also has its own game-specific rules - check the rules page on that game's site.",
          )}
        </p>

        <div className="mx-auto mt-12 grid max-w-4xl grid-cols-1 gap-5 sm:grid-cols-3">
          {pillars.map((rule, i) => (
            <div
              key={rule.title}
              className="flex flex-col rounded-2xl border border-stone-line bg-white p-6 shadow-card transition-all duration-200 hover:-translate-y-1 hover:border-uliunai-red/30 hover:shadow-card-hover"
            >
              <span className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-uliunai-red/10 font-orbitron text-base font-black text-uliunai-red">
                {i + 1}
              </span>
              <h3 className="font-orbitron text-lg font-bold text-stone-900">{t(rule.title)}</h3>
              <p className="mt-2 flex-1 font-rajdhani text-sm leading-relaxed text-stone-600">
                {t(rule.summary)}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/conduct"
            className="group inline-flex items-center gap-2 rounded-full bg-uliunai-red px-7 py-3 font-orbitron text-sm font-bold uppercase tracking-wider text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-uliunai-red-dark hover:shadow-glow"
          >
            {t('Read the full Code of Conduct')}
            <ArrowUpRight
              size={16}
              className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </Link>
        </div>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------------- */

/** Render a row of social link pills (used by Hero). */
export function SocialLinks({
  social,
  variant = 'light',
}: {
  social: CommunityData['social']
  variant?: 'light' | 'dark'
}) {
  if (variant === 'dark') {
    return (
      <>
        {social.map((s) => {
          const Icon = socialIcon(s.icon)
          return (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={s.label}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-zinc-400 transition-all duration-200 hover:-translate-y-0.5 hover:border-uliunai-red/50 hover:text-uliunai-red"
            >
              <Icon size={18} />
            </a>
          )
        })}
      </>
    )
  }

  return (
    <>
      {social.map((s) => {
        const Icon = socialIcon(s.icon)
        return (
          <a
            key={s.label}
            href={s.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={s.label}
            className="group inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/70 px-5 py-2.5 font-rajdhani text-sm font-semibold text-zinc-800 backdrop-blur transition-all duration-200 hover:-translate-y-0.5 hover:border-uliunai-red/50 hover:text-uliunai-red hover:shadow-glow"
          >
            <Icon
              size={18}
              className="text-zinc-500 transition-colors group-hover:text-uliunai-red"
            />
            {s.label}
          </a>
        )
      })}
    </>
  )
}
